import { Tag } from 'lucide-react'
import type { LabelDocument } from '@/lib/db/rxdb/schemas/label'
import { cn } from '@/lib/utils'

interface TaskLabelsDisplayProps {
  labels: Array<LabelDocument>
  onClick?: () => void
  className?: string
  size?: 'sm' | 'md'
}

/**
 * TaskLabelsDisplay - Displays task labels with different states:
 * - Empty: Shows tag icon with border
 * - Single label: Shows the label with colored dot and name
 * - Multiple labels: Shows colored dots + count
 *
 * @example
 * <TaskLabelsDisplay labels={labels} onClick={() => setOpen(true)} />
 */
export function TaskLabelsDisplay({
  labels,
  onClick,
  className,
  size = 'sm',
}: TaskLabelsDisplayProps) {
  const filteredLabels = labels.filter((label) => !label.isDeleted)
  const labelCount = filteredLabels.length

  // Empty state: Don't show anything
  if (labelCount === 0) {
    return null
  }

  // Single label: Display the label badge with white background
  const label = filteredLabels[0]
  if (labelCount === 1 && label) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full border border-input bg-card pl-2.5 pr-1.5 text-foreground font-medium transition-colors hover:border-border hover:bg-secondary',
          size === 'sm' ? 'h-6 text-xs' : 'h-8 text-sm',
          className,
        )}
        aria-label={`Label: ${label.name}`}
      >
        {/* Color dot indicator */}
        <span
          className={cn(
            'rounded-full shrink-0',
            size === 'sm' ? 'size-2' : 'size-2.5',
          )}
          style={{ backgroundColor: label.color }}
        />

        {/* Label name */}
        <span className="truncate max-w-[120px]">{label.name}</span>

        {/* Add icon */}
        <Tag
          className={cn(
            'shrink-0 text-muted-foreground/70',
            size === 'sm' ? 'size-3.5' : 'size-4',
          )}
        />
      </button>
    )
  }

  // Multiple labels: Display colored dots + count
  const visibleLabels = filteredLabels.slice(0, 3) // Show max 3 dots

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-sm border border-input bg-card px-2 text-foreground transition-colors hover:border-border hover:bg-secondary',
        size === 'sm' ? 'h-6 text-xs' : 'h-8 text-sm',
        className,
      )}
      aria-label={`${labelCount} labels`}
    >
      {/* Colored dots */}
      <div className="flex items-center -space-x-1">
        {visibleLabels.map((label, index) => (
          <span
            key={label.id}
            className={cn(
              'rounded-full border border-card shrink-0',
              size === 'sm' ? 'size-2' : 'size-2.5',
            )}
            style={{
              backgroundColor: label.color,
              zIndex: visibleLabels.length - index,
            }}
          />
        ))}
      </div>

      {/* Label count */}
      <span className="font-medium">
        {labelCount} {labelCount === 1 ? 'label' : 'labels'}
      </span>
    </button>
  )
}
